<template>
    <div class="row">
        <div class="col">
            <sub-section title="What you want from your plan" :showHelp="false">

                <div class="test-scenarios row text-center d-flex justify-content-center">
                    <div class="col-xl-4">
                        <div class="icon mx-auto">
                            <i class="bi bi-calendar-heart"></i>
                        </div>
                        <p class="text-center">
                            You will retire in <strong>{{ model.retirementDate }}</strong> and draw down your savings and pension pots from that date.
                        </p>
                    </div>
                    <div class="col-xl-4 offsetxl-1">
                        <div class="icon mx-auto">
                            <composite-icon :icons="[ { icon: 'bi-circle', size: '3.5rem' }, { icon: 'bi-currency-pound', size: '2.2rem', top: '0.95rem', left: '0.63rem' } ]"></composite-icon>
                        </div>
                        <p v-html="whatYouWantInfo.incomeDesc"></p>
                    </div>
                    <div class="col-xl-4">
                        <div class="icon mx-auto">
                            <i class="bi bi-percent"></i>
                        </div>
                        <p v-if="whatYouWantInfo.inflationDesc != ''">
                            {{ whatYouWantInfo.inflationDesc }}
                        </p>
                    </div>
                </div>

                <div class="test-scenarios row text-center d-flex justify-content-center">
                    <div class="col-xl-4">
                        <div class="icon mx-auto">
                            <i class="bi bi-cash mb-4"></i>
                        </div>
                        <p>{{ model.taxFreeChoiceText }}</p>
                    </div>
                    <div class="col-xl-4">
                        <div class="icon mx-auto">
                            <composite-icon :icons="[{ icon: 'bi-piggy-bank', size: '3.5rem' }, { icon: 'bi-arrow-up-right', size: '1.7rem', top: '0.8rem', left: '3.5rem' }]"></composite-icon>
                        </div>
                        <p>You have used an estimated total return, after charges, of <strong>{{ model.returnOnPensions }}%</strong> a year on your pension pots.</p>
                    </div>
                    <div class="col-xl-4">
                        <div class="icon mx-auto">
                            <composite-icon :icons="[{ icon: 'bi-bank', size: '3.5rem' }, { icon: 'bi-arrow-up-right', size: '1.7rem', top: '0.8rem', left: '3.5rem' }]"></composite-icon>
                        </div>
                        <p>You have used an estimated total return, after charges, of <strong>{{ model.returnOnSavings }}%</strong> a year on your savings pots.</p>
                    </div>
                </div>

                <div class="test-scenarios row text-center d-flex justify-content-center">
                    <div class="col-xl-4" v-if="model.showPensionContribText">
                        <div class="icon mx-auto">
                            <i class="bi bi-credit-card mb-4"></i>
                        </div>
                        <p>
                            You will make total monthly contributions of <strong>{{ model.monthlyPensionContribTotal }}</strong> per month to your pension pots{{ model.contribIncreaseText }}.
                        </p>
                    </div>
                    <div class="col-xl-4" v-if="model.showProtectPotText">
                        <div class="icon mx-auto">
                            <i class="bi bi-shield-lock"></i>
                        </div>
                        <p>
                            You have chosen to protect <strong>{{ model.protectedPot }}</strong> of your pensions pots throughout your retirement and not
                            reduce your pension pots below this level.
                        </p>
                    </div>
                    <div class="col-xl-4" v-if="model.giSubscriber">
                        <div class="icon mx-auto">
                            <i class="bi bi-bag mb-4"></i>
                        </div>
                        <p>
                            You have chosen to use <strong>{{ model.giPercentAlloc }}%</strong> of your <strong>{{ model.giRemainingText }}</strong> pension pots to model a
                            potential purchase of a lifetime guaranteed income<sup>**</sup>.
                        </p>
                    </div>
                </div>

                <ul class="list-unstyled text-muted small mb-5">
                    <li class="mb-3"><sup>*</sup> We've assumed you'll spend this and it has not been used in this design.</li>
                    <li v-if="model.giSubscriber">
                        <sup>**</sup> Based on illustrative rates the annual guaranteed income purchased may be {{ model.giAnnualInc }}. This is an
                        illustration only and the actual amount you would receive could be higher or lower. Actual rates will depend on
                        your age when you apply, plus they will be personalised to you, so other factors such as your health, lifestyle
                        and where you live will determine the final rate.
                    </li>
                </ul>

                <WhatYouHave :retiree="retiree"></WhatYouHave>
            </sub-section>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { FinishInfoResponse } from '@/guiide/ts/response/finishInfoResponse';
import { Retiree } from "@/guiide/ts/retiree";
import { formatter } from "@/shared/ts/formatter";
import { retireeDescriber } from "@/guiide/ts/retireeDescriber";
import Constants from "@/guiide/ts/constants";
import SubSection from '../common/SubSection.vue';
import WhatYouHave from "@/guiide/components/finish/WhatYouHave.vue"
import CompositeIcon from "@/guiide/components/common/CompositeIcon.vue";

type WhatYouWantInfo = { retirementDate: string, incomeDesc: string, inflationDesc: string };

const props = defineProps({
    retiree: {
        type: Object as () => Retiree | null,
        default: () => null,
    },
    finishInfo: {
        type: Object as () => FinishInfoResponse,
        default: () => new FinishInfoResponse(false, 0, []),
    },
});

const whatYouWantInfo = computed<WhatYouWantInfo>(() => {
    const retiree = props.retiree;
    return retiree != null
        ? {
            retirementDate: formatter.monthAndYearDate(new Date(retiree.retirementDate)),
            incomeDesc: retireeDescriber.incomeStatement(),
            inflationDesc: retireeDescriber.inflationStatement()
        }
        : { retirementDate: '', incomeDesc: '', inflationDesc: '' };
});

type AssumptionsInfo = {
    retirementDate: string, taxFreeChoiceText: string, giPercentAlloc: string, giRemainingText: string, giSubscriber: boolean,
    giAnnualInc: string, returnOnPensions: string, returnOnSavings: string, protectedPot: string, showProtectPotText: boolean,
    showPensionContribText: boolean, monthlyPensionContribTotal: string, contribIncreaseText: string
};

const model = computed<AssumptionsInfo>(() => {
    const retiree = props.retiree;

    if (retiree == null) {
        return {
            retirementDate: '', taxFreeChoiceText: '', giSubscriber: false, giPercentAlloc: '', giRemainingText: '', giAnnualInc: '',
            returnOnPensions: '', returnOnSavings: '', protectedPot: '', showProtectPotText: false, showPensionContribText: false,
            monthlyPensionContribTotal: '', contribIncreaseText: ''
        };
    }

    const monthlyContributionAmount:number = props.retiree?.pensionPots.pots.reduce((accumulator, value) => accumulator += value.monthlyContribution, 0) ?? 0;

    const contribIncreaseText = retiree.pensionPots.pots.some(p => p.willTheseIncreaseIfSalaryIncreases)
        ? " and increase these contributions as your salary goes up"
        : "";

    return {
            retirementDate: formatter.monthAndYearDate(new Date(retiree.retirementDate)),
            taxFreeChoiceText: getTaxFreeChoiceText(retiree.requirements.takingTaxFreeAsLumpSum),
            giSubscriber: retiree.guaranteedIncome.subscribed,
            giPercentAlloc: retiree.guaranteedIncome.allocation.toString(),
            giRemainingText: retiree.requirements.takingTaxFreeAsLumpSum ? 'remaining ' : '',
            giAnnualInc: formatter.formatCurrency(retiree.guaranteedIncome.annualIncome),
            returnOnPensions: retiree.returnOnPensions.toString(),
            returnOnSavings: retiree.returnOnSavings.toString(),
            protectedPot: formatter.formatCurrency(retiree.protectedPot),
            showProtectPotText: retiree.protectedPot > 0,
            showPensionContribText: monthlyContributionAmount > 0,
            monthlyPensionContribTotal: formatter.formatCurrency(monthlyContributionAmount),
            contribIncreaseText: contribIncreaseText
        }
});

function getTaxFreeChoiceText(asLumpSum: boolean): string {
    const taxFreeCashLimit = formatter.formatCurrency(Constants.TaxFreeCashLimit);
    return asLumpSum
        ? `You've chosen to take 25% of your pension pots (up to a maximum of ${taxFreeCashLimit}) as a tax free cash lump sum immediately*.`
        : "You've chosen not to take the 25% tax free lump sum immediately from your pension pots. We've used this in this design and assumed 25% of the income you take from your pension pots will not be subject to tax.";
}
</script>

<style scoped>
    .test-scenarios p {
        font-size: 16px !important;
    }

    .icon i {
        font-size: 3.5rem;
    }
</style>